import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import BBALoader from '../components/BBALoader';
import PortalBackground from '../components/PortalBackground';

/**
 * Return page for Implementation Hours one-time purchases. Always redirects to dashboard
 * (never programs).
 *
 * Landing here is not proof of payment, so the hours are never granted by this page — it asks the
 * server, which reads the session straight from Stripe. Card payments are settled by the time they
 * get here; ACH debits are not, and take a few business days to clear, so those get an honest
 * "on the way" message instead of a checkmark. Either way the Stripe webhook credits the hours on
 * its own; this call only saves the client from waiting on webhook delivery.
 */
const StrategyCallCheckoutReturn = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const sessionId = searchParams.get('session_id');
  const isPortal = window.location.pathname.startsWith('/portal');
  const dashboardPath = isPortal ? '/portal' : '/start/dashboard';

  // cancelled | verifying | credited | pending | unknown
  const [status, setStatus] = useState(sessionId ? 'verifying' : 'cancelled');
  const [hours, setHours] = useState(null);
  const hasVerifiedRef = useRef(false);

  const handleGoToDashboard = () => {
    navigate(dashboardPath, { state: { paymentCompleted: status === 'credited' } });
  };

  const verify = useCallback(async () => {
    try {
      const verifyPurchase = httpsCallable(functions, 'verifyImplementationHoursPurchase');
      const { data } = await verifyPurchase({ sessionId });
      if (data?.state === 'credited') {
        setHours(typeof data.hours === 'number' ? data.hours : null);
        setStatus('credited');
      } else if (data?.state === 'pending') {
        setStatus('pending');
      } else {
        // Paid but we couldn't place the hours on an account — the webhook logs this loudly and
        // it needs a human, so don't claim success.
        setStatus('unknown');
      }
    } catch (error) {
      // The webhook is still the authority; a failed check here doesn't mean a failed purchase.
      console.error('Error verifying implementation hours purchase:', error);
      setStatus('unknown');
    }
  }, [sessionId]);

  useEffect(() => {
    if (!sessionId || hasVerifiedRef.current) return;
    if (!currentUser) {
      // AuthProvider renders children only once auth has resolved, so no user here means their
      // session lapsed during the Stripe redirect. The callable requires auth — but the webhook
      // credits the hours regardless, so say so rather than spinning forever.
      setStatus('unknown');
      return;
    }
    hasVerifiedRef.current = true;
    verify();
  }, [sessionId, currentUser, verify]);

  const buttonClass =
    'px-6 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors';

  const renderBody = () => {
    switch (status) {
      case 'verifying':
        return (
          <>
            <div className="flex justify-center mb-4">
              <BBALoader size={64} />
            </div>
            <h1 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-white mb-2">
              Confirming your purchase…
            </h1>
            <p className="text-gray-600 dark:text-gray-400">Hang tight while we check with Stripe.</p>
          </>
        );
      case 'credited':
        return (
          <>
            <div className="text-6xl mb-4 text-green-600">✓</div>
            <h1 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-white mb-2">
              Implementation hours purchased!
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              {hours
                ? `Thank you for your purchase. ${hours} additional ${hours === 1 ? 'hour has' : 'hours have'} been added to this period.`
                : 'Thank you for your purchase. Your additional implementation hours have been added to your account.'}
            </p>
            <button onClick={handleGoToDashboard} className={buttonClass}>
              Go to Dashboard
            </button>
          </>
        );
      case 'pending':
        return (
          <>
            <div className="text-6xl mb-4">🏦</div>
            <h1 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-white mb-2">
              Bank payment on its way
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Thank you for your purchase. Bank transfers take 2–5 business days to clear — your
              additional implementation hours are added automatically as soon as the payment lands,
              and you'll see them on your dashboard then.
            </p>
            <button onClick={handleGoToDashboard} className={buttonClass}>
              Go to Dashboard
            </button>
          </>
        );
      case 'unknown':
        return (
          <>
            <h1 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-white mb-2">
              We're still confirming this
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Your payment may well have gone through — we just couldn't confirm it from here. Your
              hours will appear on your dashboard once it settles. If they haven't shown up shortly,
              reach out to your success manager and we'll sort it out.
            </p>
            <button onClick={handleGoToDashboard} className={buttonClass}>
              Go to Dashboard
            </button>
          </>
        );
      default:
        return (
          <>
            <h1 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-white mb-2">
              Checkout cancelled
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              No worries. You can add implementation hours anytime from your dashboard.
            </p>
            <button onClick={handleGoToDashboard} className={buttonClass}>
              Back to Dashboard
            </button>
          </>
        );
    }
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center p-4">
      <PortalBackground />
      <div className="relative max-w-md w-full rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm p-8 text-center">
        {renderBody()}
      </div>
    </div>
  );
};

export default StrategyCallCheckoutReturn;
